import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { KeysResult, KeysResults } from 'fuzzysort';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { FuzzySortResultHighlightComponent } from 'app/shared/fuzzysort-helper/fuzzy-sort-result-highlight.component';
import { emptyFuzzySortOnKeys, executeFuzzySortOnKeys, KeysOfUnion } from 'app/shared/fuzzysort-helper/fuzzysort-helper';
import { GlobalPermission, Subscription, SubscriptionPermission } from 'app/shared/graph';
import { CopyToClipboardComponent } from 'app/shared/utilities/copy-to-clipboard.component';

import { AddSubscriptionDialogComponent, AddSubscriptionDialogResult } from './add-subscription-dialog.component';
import { SubscriptionAdminService } from './subscription-admin.service';

@Component({
    selector: 'admin-subscriptions-page',
    templateUrl: './subscriptions-page.component.html',
    styleUrls: ['./subscriptions-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatIconButton, MatMenuTrigger, MatIcon, MatMenu, MatMenuItem, MatFormField, MatPrefix, MatLabel, MatInput, FormsModule, MatChipRemove, MatButton, MatTable, MatColumnDef, MatHeaderCell, MatCell, RouterLink, CopyToClipboardComponent, MatHeaderRow, MatRow, MatHeaderCellDef, MatCellDef, MatHeaderRowDef, MatRowDef, MatChipListbox, MatChipOption, FuzzySortResultHighlightComponent]
})
export class SubscriptionsPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly subscriptionAdminService = inject(SubscriptionAdminService);
    private readonly authorizationService = inject(AuthorizationService);
    private readonly dialog = inject(MatDialog);

    @ViewChild('subscriptionTable', { static: false })
    subscriptionTable: MatTable<any>;

    subscriptions: Subscription[] = [];
    fuzzySearchKeys: Array<KeysOfUnion<Subscription>> = ['id', 'label'];
    displayedSubscription: KeysResults<Subscription> = emptyFuzzySortOnKeys();
    authContext: AuthorizationContext;

    filter: {
        name?: string;
        showBillableOnly: boolean;
        showActiveOnly: boolean,
    } = {
        showActiveOnly: true,
        showBillableOnly: false
    };
    displayedColumns: string[] = [
        'label',
        'contact',
        'billable',
        'storeCount'
    ];

    ngOnInit(): void {
        this.authorizationService.authorizationContext().subscribe((authContext) => {
            this.authContext = authContext;
        });
        const savedFilter = localStorage.getItem('subscriptions-page-filter');
        if (savedFilter) {
            this.filter = { ...this.filter, ...JSON.parse(savedFilter) as any };
        }
        this.subscriptionAdminService.list().subscribe({
            next: subscriptions => {
                this.subscriptions = subscriptions;
                this.pageStatus = PageStatus.loaded;
                this.updateDisplayedSubscription();
            },
            error: err => {
                this.pageError = err;
                this.pageStatus = PageStatus.loadError;
            }
        });
    }

    ngOnDestroy(): void {
        this.unsubscribeSubscriptions();
    }

    updateDisplayedSubscription() {
        const visibleSubscriptions = this.subscriptions
            .filter(s => this.isSubscriptionVisible(s))
            .sort((a, b) => a.label.localeCompare(b.label));
        this.displayedSubscription = executeFuzzySortOnKeys(this.filter.name ?? '', visibleSubscriptions, this.fuzzySearchKeys)
        this.subscriptionTable.renderRows();
    }

    toggleShowActiveOnly() {
        this.filter.showActiveOnly = !this.filter.showActiveOnly;
        this.updateFilter();
    }

    toggleShowBillableOnly() {
        this.filter.showBillableOnly = !this.filter.showBillableOnly;
        this.updateFilter();
    }

    public updateFilter() {
        localStorage.setItem('subscriptions-page-filter', JSON.stringify({ ...this.filter, name: undefined }));
        this.updateDisplayedSubscription();
    }

    private isSubscriptionVisible(s: Subscription) {
        if (this.filter.showBillableOnly) {
            if (!s.billing?.billingStartDateUtc) {
                return false;
            }
        }
        if (this.filter.showActiveOnly) {
            if (!s.active) {
                return false;
            }
        }
        return true;

    }

    subscriptionId = (i: number, s: KeysResult<Subscription>) => {
        return (s.obj as Subscription).id;
    }

    canReadInvoice(subscription: Subscription): boolean {
        return this.authContext.hasSubscriptionPermissions(subscription.id, SubscriptionPermission.ReadInvoices);
    }

    $subscription(subscription: any): Subscription {
        return subscription as Subscription;
    }

    canCreateSubscriptions() {
        return this.authContext.hasGlobalPermissions(GlobalPermission.CreateSubscription);
    }

    reload(){
        this.subscribeWithGenericLoadingErrorHandling(this.subscriptionAdminService.list(), subscriptions => {
            this.subscriptions = subscriptions;
        });
    }
    addSubscriptions() {
        this.dialog.open<AddSubscriptionDialogComponent, void, AddSubscriptionDialogResult>(AddSubscriptionDialogComponent).afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.subscribeWithGenericSavingErrorHandling(this.subscriptionAdminService.createSubscription({
                label: result.subscriptionLabel,
            }), (response) => {
                this.subscriptions.push(response);
                this.updateDisplayedSubscription()
            });
        });
    }
}
